import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  isPushSupported,
  getPushPermission,
  requestNotificationPermission,
  completePushSubscription,
  isPushSubscribed,
} from "@/lib/pushNotifications";
import toast from "react-hot-toast";

const DISMISS_KEY = "reshet_push_prompt_dismissed";

function wasDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch (_) {}
}

export default function PushNotificationPrompt({ enabled = true }) {
  const [mode, setMode] = useState(null); // null | "prompt"
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !isPushSupported()) {
      setMode(null);
      return;
    }

    if (wasDismissed()) {
      setMode(null);
      return;
    }

    (async () => {
      const permission = getPushPermission();

      if (permission === "granted") {
        completePushSubscription().catch(() => {});
        setMode(null);
        return;
      }

      if (permission === "denied") {
        setMode(null);
        return;
      }

      const subscribed = await isPushSubscribed();
      if (subscribed) {
        setMode(null);
        return;
      }

      setMode("prompt");
    })();
  }, [enabled]);

  const handleEnable = () => {
    if (!isPushSupported()) {
      toast.error("הדפדפן לא תומך בהתראות Push");
      return;
    }

    setLoading(true);

    requestNotificationPermission()
      .then(() => completePushSubscription())
      .then(() => {
        rememberDismissed();
        toast.success("התראות Push הופעלו");
        setMode(null);
      })
      .catch((err) => {
        toast.error(err?.message || "לא ניתן להפעיל התראות");
        if (getPushPermission() === "denied") {
          rememberDismissed();
          setMode(null);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDismiss = () => {
    rememberDismissed();
    setMode(null);
  };

  if (!mode) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto lg:bottom-6 lg:left-6 lg:right-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-emerald-100 p-4 flex gap-3 items-start">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-100">
          <Bell className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm">הפעלת התראות</p>
          <p className="text-xs text-gray-600 mt-1">
            קבל התראות על מבצעים חדשים, חובת הזמנה ועוד
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleEnable}
              disabled={loading}
            >
              {loading ? "מפעיל..." : "הפעל התראות"}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              אחר כך
            </Button>
          </div>
        </div>
        <button type="button" onClick={handleDismiss} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
